import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import LdsLillyLogo from '../LdsLillyLogo/index.jsx';

function LdsFooter({
  primaryLinksColumnFlow = 'even',
  lillyLogo = {
    size: 'md',
    className: 'pl-0 pr-0 pb-0',
  },
  className = '',
  children,
  ...rest
}) {
  const childComponents = React.Children.toArray(children);

  let primaryNavFlow = 'even';
  if (primaryLinksColumnFlow.toLowerCase() === 'auto') primaryNavFlow = 'auto';

  const hasSocial = childComponents.some(
    (child) => child.type === LdsFooter.SocialMedia
  );

  const hasPrimaryLinks = childComponents.some(
    (child) => child.type === LdsFooter.PrimaryLinks
  );

  const hasSupportInfo = childComponents.some(
    (child) => child.type === LdsFooter.SupportInfo
  );

  const hasLegalInfo = childComponents.some(
    (child) => child.type === LdsFooter.LegalInfo
  );

  const hasGlobalLinks = childComponents.some(
    (child) => child.type === LdsFooter.GlobalLinks
  );

  const hasLogoOverride = childComponents.some(
    (child) => child.type === LdsFooter.LogoOverride
  );

  const hasCustomOverride = childComponents.some(
    (child) => child.type === LdsFooter.CustomOverride
  );

  let classTopRow = 'justify-content-center justify-content-md-between';
  if (!hasPrimaryLinks && hasSupportInfo) {
    classTopRow = 'justify-content-center justify-content-md-end';
  }

  const topRowGlobalLinks = (children) => {
    return children.map((child, index) => {
      return (
        <li
          key={index}
          className="col-12 col-md d-md-none"
        >
          {child}
        </li>
      );
    });
  };

  return (
    <footer
      className={cn(
        'lds-footer',
        className
      )}
      {...rest}
    >
      <div className='wrapper'>
        {hasCustomOverride && (
          <div className="lds-footer-custom-content">
            {childComponents
              .filter((child) => child.type === LdsFooter.CustomOverride)
              .map((child) => child.props.children)}
          </div>
        )}
        {(hasPrimaryLinks || hasGlobalLinks || hasSupportInfo) && (
        <div className={cn(
          'lds-footer-row-top',
          'row',
          classTopRow
        )}
        >
          {/* Primary links section */}
          {(hasPrimaryLinks || hasGlobalLinks) && (
            <div className={cn(
              'lds-footer-main-links',
              'col-12',
              'col-md-9'
            )}>
              <ul className={cn(
                'lds-footer-list',
                `lds-footer-list-${primaryNavFlow}`
              )}>
                {childComponents
                  .filter((child) => child.type === LdsFooter.PrimaryLinks)
                  .map((child) => child.props.children)}
              </ul>
              {/* Global links section - mobile only */}
              { hasGlobalLinks && (
                <ul className={cn(
                  'global-content'
                )}>
                  {childComponents
                    .filter((child) => child.type === LdsFooter.GlobalLinks)
                    .map((child) => topRowGlobalLinks(child.props.children))}
                </ul>
              )}
            </div>
          )}
          {/* Support section */}
          {hasSupportInfo && (
            <div className={cn(
              'col-12',
              'col-md-3',
              'lds-footer-support'
            )}>
              {childComponents
                .filter((child) => child.type === LdsFooter.SupportInfo)
                .map((child) => child.props.children)}
            </div>
          )}
        </div>
        )}
        <div className={cn(
          'lds-footer-row-meta',
          'row'
        )}
        >
          {/* Legal/meta section */}
          {hasLegalInfo && (
            <div className={cn(
              'lds-footer-meta',
              'col-12'
            )}>
              {childComponents
                .filter((child) => child.type === LdsFooter.LegalInfo)
                .map((child) => child.props.children)}
            </div>
          )}
        </div>
        <div className={cn(
          'lds-footer-row-bottom',
          'row'
        )}
        >
          <div className={cn(
            'col-12',
            'col-md-auto',
            'order-md-2',
            'ms-auto'
          )}>
            {/* Social section */}
            {hasSocial && (
              <div className="lds-footer-social-icons">
                {childComponents
                  .filter((child) => child.type === LdsFooter.SocialMedia)
                  .map((child) => child.props.children)}
              </div>
            )}
            {/* Global links section */}
            {hasGlobalLinks && (
              <div className={cn(
                'lds-footer-global-links',
                'd-none',
                'd-md-block'
              )}>
                {childComponents
                  .filter((child) => child.type === LdsFooter.GlobalLinks)
                  .map((child) => child.props.children)}
              </div>
            )}
          </div>
          {/* Logo section */}
          <div
            className={cn(
              'col-12',
              'col-md-auto',
              'order-md-1',
              'lds-footer-logo'
            )}
          >
              { hasLogoOverride ? (
                <div className="footer-override">
                  {childComponents
                    .filter((child) => child.type === LdsFooter.LogoOverride)
                    .map((child) => child.props.children)}
                </div>
              ) : (
                <LdsLillyLogo {...lillyLogo} />
              )}
          </div>
        </div>
      </div>
    </footer>
  );
}

LdsFooter.PrimaryLinks = function LdsFooterPrimaryLinks({
  children,
  ...rest
}) {
  return (
    <div
      className="row"
      {...rest}
    >
      {children}
    </div>
  );
};

LdsFooter.PrimaryLinks.List = function LdsFooterPrimaryLinksList({
  className = '',
  children,
  ...rest
}) {
  return (
    <ul
      className={cn(
        className
      )}
      {...rest}
    >
      {children}
    </ul>
  );
};

LdsFooter.PrimaryLinks.Item = function LdsFooterPrimaryLinksItem({
  className = '',
  children,
  ...rest
}) {
  return (
    <li
      className={cn(
        'lds-footer-links-item',
        className
      )}
      {...rest}
    >
      {children}
    </li>
  );
};

LdsFooter.SocialMedia = function LdsFooterSocialMediaContent({ children }) {
  return <>{children}</>;
};

LdsFooter.SupportInfo = function LdsFooterSupportInfoContent({ children }) {
  return <>{children}</>;
};

LdsFooter.LegalInfo = function LdsFooterLegalInfoContent({ children }) {
  return <>{children}</>;
};

LdsFooter.GlobalLinks = function LdsFooterGlobalLinksContent({ children }) {
  return <>{children}</>;
};

LdsFooter.LogoOverride = function LdsFooterLogoOverrideContent({ children }) {
  return <>{children}</>;
};

LdsFooter.CustomOverride = function LdsFooterCustomOverrideConten({ children }) {
  return <>{children}</>;
};

// Prop Types
LdsFooter.propTypes = {
  primaryLinksColumnFlow: PropTypes.oneOf(['even', 'auto']),
  lillyLogo: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
};
LdsFooter.PrimaryLinks.propTypes = {
  children: PropTypes.node,
};
LdsFooter.PrimaryLinks.Item.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};
LdsFooter.PrimaryLinks.List.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};
LdsFooter.SocialMedia.propTypes = {
  children: PropTypes.node,
};
LdsFooter.SupportInfo.propTypes = {
  children: PropTypes.node,
};
LdsFooter.LegalInfo.propTypes = {
  children: PropTypes.node,
};
LdsFooter.GlobalLinks.propTypes = {
  children: PropTypes.node,
};
LdsFooter.LogoOverride.propTypes = {
  children: PropTypes.node,
};
LdsFooter.CustomOverride.propTypes = {
  children: PropTypes.node,
};

export default LdsFooter;
